import { InternalMessagesForm } from "features/CenterContent/RoleContent/InternalMessage/schema/formSchema";
import {
  ComponentProps,
  Dispatch,
  ReactElement,
  SetStateAction,
  useEffect,
  useRef,
} from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ScrollableDialogBody } from "shared/components/ScrollableBody";
import { SegmentedPanel } from "shared/components/SegmentedPanel";
import {
  Box,
  ButtonGroup,
  DialogFooter,
  DialogRoot,
  DropsBadge,
  DropsButton,
  DropsDialogContent,
  DropsDialogHeader,
  DropsStaticAlert,
  Flex,
  Stack,
  DropsTooltip,
} from "shared/components/Spor";
import { useConfirmClose } from "shared/form/useConfirmClose";
import { FCWC } from "shared/utils/FCWC";
import { css, useTheme } from "styled-components";

const delaycloseDuration = 1000;

type StepActionModalProps = {
  title: string;
  actionTitle: string;
  titleBadge?: string;
  backTitle?: string;
  cancelTitle?: string;
  withBackButton: boolean;
  // Default behavior is no backButton on first step. If onBack is provided, backButton will be shown on first step and trigger onBack when clicked.
  onBack?: () => void;
  onStep?: () => Promise<boolean>;
  onClose: () => void;
  onSubmit: () => void;
  // ↓ If enabled then submit is clickable when disabled, but runs this function instead
  onSubmitDisabled?: () => void;
  isSubmitDisabled?: boolean;
  onSubmitTooltip?: string;
  loading: boolean;
  isSuccess: boolean;
  isError: boolean;
  successMessage: string;
  failureMessage: string;
  internalMessagesFormMethods?: UseFormReturn<InternalMessagesForm>;
  trainSeries: string[];
  shouldWrapChildren?: boolean;
  subHeaders?: ReactElement[];
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
  size?: ComponentProps<typeof DialogRoot>["size"];
};

export const StepActionModal: FCWC<StepActionModalProps> = ({
  title,
  actionTitle,
  titleBadge,
  backTitle,
  cancelTitle,
  withBackButton,
  onBack,
  onClose: onCloseImmediate,
  onStep,
  onSubmit,
  onSubmitDisabled,
  isSubmitDisabled = false,
  onSubmitTooltip,
  loading,
  isSuccess,
  isError,
  successMessage,
  failureMessage,
  children,
  trainSeries,
  shouldWrapChildren = true,
  subHeaders,
  activeStep,
  setActiveStep,
  internalMessagesFormMethods: _internalMessagesFormMethods,
  size = "xl",
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const { onClose, ConfirmCloseAlert } = useConfirmClose(onCloseImmediate);

  useEffect(() => {
    if (isSuccess) {
      setTimeout(onCloseImmediate, delaycloseDuration);
    }
  }, [isSuccess]);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const showBackButton = withBackButton && (activeStep > 0 || !!onBack);
  const back = () => {
    if (activeStep === 0 && onBack) {
      onBack();
    } else {
      setActiveStep(activeStep - 1);
    }
  };
  const forward = async () => {
    if (onStep) {
      const success = await onStep();
      if (!success) return;
    }
    if (activeStep === trainSeries.length - 1) {
      onSubmit();
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  return (
    <>
      <ConfirmCloseAlert />
      <DialogRoot open size={size} onOpenChange={onClose} placement="center">
        <DropsDialogContent>
          <DropsDialogHeader>
            <Flex flexDirection="column" gap="6px">
              {title}
              {titleBadge && (
                <DropsBadge semantics="alarm" width="fit-content">
                  {titleBadge}
                </DropsBadge>
              )}
            </Flex>
          </DropsDialogHeader>
          <form id={`${title}-form`}>
            <ScrollableDialogBody
              ref={scrollRef}
              $hideScrollbar
              position="relative" // Used for absolute position for internal message
              px={0}
              py={0}
              my={0}
              border="none"
              bg="transparent" // StepActionModal uses SegmentedPanels, and therefore needs transparent background for DashedLines
            >
              <SegmentedPanel
                panelStyle={css`
                  background-color: ${theme.colorBackgroundSecondary};
                  padding: 18px;
                  border-radius: 18px;
                `}
                dashedLineVariant="secondary"
              >
                {subHeaders?.map((it, index) => (
                  // eslint-disable-next-line react/no-array-index-key -- We dont have anything else to go by
                  <Stack key={index}>{it}</Stack>
                ))}
                {shouldWrapChildren && (
                  <Stack alignItems="center">{children}</Stack>
                )}
              </SegmentedPanel>
              {!shouldWrapChildren && children}
            </ScrollableDialogBody>
          </form>

          <DialogFooter
            justifyContent="space-between"
            width="100%"
            pb="24px"
            flexDir="column"
          >
            {isError && (
              <DropsStaticAlert w="100%" variant="error">
                {failureMessage}
              </DropsStaticAlert>
            )}
            {isSuccess && (
              <DropsStaticAlert
                w="100%"
                variant="success"
                data-testid="action-modal-success-msg"
              >
                {successMessage}
              </DropsStaticAlert>
            )}
            <ButtonGroup justifyContent="space-between" mt="12px" w="100%">
              <DropsButton
                type="button"
                variant="tertiary"
                onClick={(e) => {
                  e.preventDefault();
                  onClose();
                }}
              >
                {cancelTitle || t("Avbryt")}
              </DropsButton>
              <Flex gap="12px">
                {showBackButton && (
                  <DropsButton type="button" variant="tertiary" onClick={back}>
                    {backTitle || `← ${t("Tilbake")}`}
                  </DropsButton>
                )}
                {activeStep < trainSeries.length - 1 ? (
                  <DropsTooltip label={onSubmitTooltip}>
                    <Box>
                      <DropsButton
                        disabled={isSubmitDisabled}
                        variant="secondary"
                        onClick={forward}
                      >
                        {t("Neste togserie")} →
                      </DropsButton>
                    </Box>
                  </DropsTooltip>
                ) : (
                  <DropsTooltip label={onSubmitTooltip}>
                    <Box>
                      <DropsButton
                        data-testid="modal_submitButton"
                        aria-invalid={isSubmitDisabled}
                        form={`${title}-form`}
                        disabled={
                          isSuccess || (isSubmitDisabled && !onSubmitDisabled)
                        }
                        variant="primary"
                        onClick={async (e) => {
                          /**
                           * Moved <form>'s onSubmit() to here since it caused
                           * a validation when "Opprett hendelse" button loaded
                           */
                          if (isSubmitDisabled && onSubmitDisabled) {
                            onSubmitDisabled();
                            e.nativeEvent.preventDefault(); // Prevent page refresh
                            return;
                          }
                          await forward();
                          e.nativeEvent.preventDefault(); // Prevent page refresh
                        }}
                        loading={loading}
                      >
                        {isError ? t("Prøv igjen") : actionTitle}
                      </DropsButton>
                    </Box>
                  </DropsTooltip>
                )}
              </Flex>
            </ButtonGroup>
          </DialogFooter>
        </DropsDialogContent>
      </DialogRoot>
    </>
  );
};
